import { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ShieldCheck, Wrench, Award } from "lucide-react";
import axios from "axios";
import { SEO } from "@/components/SEO";
import { HoverImage } from "@/components/HoverImage";

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => n?.toLocaleString("en-NZ");

// Small fixed vehicle/type taxonomy — looked up against each product's `vehicle`/`type` key,
// not stored per-product (mirrors the Roof Racks page's vehicle taxonomy)
const BULL_BAR_VEHICLES = [
  { key: "lc70", label: "Land Cruiser 70 Series", short: "Land Cruiser 70 Series", note: "70 · 76 · 78 · 79 · All years — select year at checkout" },
  { key: "hilux", label: "Toyota Hilux", short: "Hilux (2016-2025)", note: "2016 to 2025" },
  { key: "ranger", label: "Ford Ranger", short: "Ranger (2022+)", note: "New-Gen 2022+" },
  { key: "jimny", label: "Suzuki Jimny", short: "Jimny", note: "Bars, sliders, ladders" },
  { key: "gwm-cannon", label: "GWM Cannon", short: "GWM Cannon", note: "Cannon & Alpha" },
  { key: "byd-shark", label: "BYD Shark", short: "BYD Shark", note: "PHEV ute" },
  { key: "gladiator", label: "Jeep Gladiator", short: "Gladiator", note: "New for NZ" },
  { key: "lc200", label: "Land Cruiser 200 Series", short: "LC 200 Series", note: "ONCA barwork · built to order" },
  { key: "lc300", label: "Land Cruiser 300 Series", short: "LC 300 Series", note: "ONCA barwork · built to order" },
];
const BULL_BAR_TYPES = [
  { key: "front-bar", label: "Front Bars" },
  { key: "rear-bar", label: "Rear Bars" },
  { key: "slider", label: "Rock Sliders" },
  { key: "wellside", label: "Wellsides" },
  { key: "accessory", label: "Accessories" },
];

const HERO = "https://static.wixstatic.com/media/731077_89595e29db4e4acf844bc0b428f26375~mv2.jpg/v1/fill/w_1920,h_1080,al_c,q_85,enc_avif,quality_auto/731077_89595e29db4e4acf844bc0b428f26375~mv2.jpg";

const Card = ({ m, testid }) => (
  <Link
    to={`/bull-bars/${m.slug}`}
    data-testid={testid}
    className="group relative block overflow-hidden border border-[var(--border-default)] hover:border-[var(--brand-primary)] transition-colors bg-[var(--bg-secondary)]"
  >
    <div className="aspect-[4/3] overflow-hidden relative">
      <HoverImage primary={m.thumb} secondary={m.gallery?.[1]?.src} alt={m.name} />
      <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-transparent" />
      {m.badge && (
        <div className="absolute top-4 left-4 flex gap-2 flex-wrap">
          <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-black bg-[var(--brand-primary)] px-2.5 py-1">
            {m.badge}
          </span>
        </div>
      )}
      <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
        <h3 className="font-display text-lg md:text-xl text-white leading-tight tracking-wide">{m.name.toUpperCase()}</h3>
        <p className="text-[11px] font-mono uppercase tracking-wider text-white/70 mt-2 line-clamp-1">{m.tagline}</p>
        <div className="flex items-end justify-between gap-3 mt-4">
          <div>
            <p className="font-display text-xl text-[var(--brand-primary)] leading-none">
              {m.priceOnRequest ? (
                <span className="text-sm font-mono uppercase tracking-widest">Enquire For Price</span>
              ) : (
                <>
                  {m.priceFrom && <span className="text-[10px] font-mono uppercase tracking-widest text-white/60 mr-1">FROM</span>}
                  ${fmt(m.price)}
                </>
              )}
            </p>
          </div>
          <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.2em] text-white group-hover:text-[var(--brand-primary)] transition-colors">
            View <ArrowUpRight size={12} />
          </span>
        </div>
      </div>
    </div>
  </Link>
);

export default function BullBarsPage() {
  const [models, setModels] = useState(null); // null = still loading
  const [selectedVehicle, setSelectedVehicle] = useState("all");

  useEffect(() => {
    axios.get(`${API}/api/products/bullbar`).then((res) => setModels(res.data)).catch((err) => { console.error(err); setModels([]); });
  }, []);

  const filtered = useMemo(() => {
    if (!models) return [];
    return models.filter((m) => selectedVehicle === "all" || m.vehicle === selectedVehicle);
  }, [models, selectedVehicle]);

  // Group filtered products by vehicle, then by type within vehicle
  const groupedByVehicle = useMemo(() => {
    return BULL_BAR_VEHICLES.map((v) => ({
      vehicle: v,
      items: filtered.filter((m) => m.vehicle === v.key),
    })).filter((g) => g.items.length > 0);
  }, [filtered]);

  if (models === null) {
    return <div className="min-h-[60vh] flex items-center justify-center text-white/50 font-mono text-sm">Loading…</div>;
  }

  return (
    <div data-testid="bull-bars-page">
      <SEO
        path="/bull-bars"
        title="Premium Bull Bars NZ · Wildog & ONCA · South African Made"
        description="South African-made ADR-tested premium bull bars, rear bars, rock sliders. Wildog & ONCA — the two brands worth having on your ute. Land Cruiser, Hilux, Ranger, Jimny, GWM Cannon, BYD Shark, Gladiator."
        image={HERO}
      />

      {/* HERO */}
      <section className="relative min-h-[45svh] md:min-h-[95svh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="Premium bull bars" className="w-full h-full object-cover object-[50%_center] md:object-[60%_center]" />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-main)] from-12% via-black/25 via-45% to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/10 to-transparent md:via-transparent" />
        </div>
        <div className="relative z-10 container-x pb-4 md:pb-20 pt-20 md:pt-32 w-full">
          <p className="overline mb-2 md:mb-6 text-[9px] md:text-xs">— ONCA Off-Road · Wildog South Africa</p>
          <h1 className="font-display text-[7.5vw] md:text-[8vw] lg:text-[7vw] leading-[0.9] md:leading-[0.85] text-white hero-text-shadow max-w-[16ch]">
            PREMIUM<br />
            <span className="text-[var(--brand-primary)]">BULL BARS.</span><br />
            NO COMPROMISE.
          </h1>
          <p className="text-xs md:text-lg font-medium md:font-normal text-white/95 md:text-white/85 max-w-xl leading-snug md:leading-relaxed mt-3 md:mt-8 [text-shadow:0_1px_3px_rgba(0,0,0,0.9),0_2px_20px_rgba(0,0,0,0.7)] md:[text-shadow:none]">
            Not all bull bars sold in NZ can rival this build quality or perfect OEM-level fitment. We import two premium South African brands — ADR / crash tested, real crumple zones, proper engineering.
          </p>
          <div className="flex flex-wrap gap-2 md:gap-3 mt-10 md:mt-8">
            <a href="#range" className="btn-primary text-xs md:text-sm px-4 py-2.5 md:px-6 md:py-3">Shop By Vehicle <ArrowUpRight size={12} /></a>
            <a href="#enquire" className="btn-secondary text-xs md:text-sm px-4 py-2.5 md:px-6 md:py-3">Enquire</a>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="container-x py-14 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[var(--border-default)] border border-[var(--border-default)]">
          {[
            { icon: ShieldCheck, t: "ADR & Crash Tested", d: "Wildog bars sent to Australia for full crash + compliance testing. ONCA bars ADR-approved with crumple zones + airbag-compliant designs." },
            { icon: Wrench, t: "OEM-Level Fitment", d: "Proper engineering, no drilling or bodge mounts. Bars fit factory-precise off the truck to the vehicle." },
            { icon: Award, t: "Superior Build", d: "Heavy-duty tube work, premium powder coat, integrated winch/light mounts. Built for real use, not showroom photos." },
          ].map((p, i) => {
            const Icon = p.icon;
            return (
              <div key={i} className="bg-[var(--bg-main)] p-6 md:p-8" data-testid={`bull-bars-pillar-${i}`}>
                <Icon size={26} className="text-[var(--brand-primary)] mb-5" strokeWidth={1.3} />
                <h3 className="font-display text-xl text-white tracking-wide">{p.t}</h3>
                <p className="text-[var(--text-secondary)] text-sm mt-3 leading-relaxed">{p.d}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* VEHICLE PICKER */}
      <section id="range" className="container-x pb-8 md:pb-10 scroll-mt-24">
        <div className="mb-8">
          <p className="overline mb-3">— Shop By Vehicle</p>
          <h2 className="font-display text-3xl md:text-5xl text-white leading-[0.95]">
            FIND WHAT FITS <span className="text-[var(--brand-primary)]">YOUR UTE.</span>
          </h2>
        </div>

        {/* Vehicle filter chips */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="vehicle-filter">
          <button
            onClick={() => setSelectedVehicle("all")}
            data-testid="vehicle-filter-all"
            className={`font-mono text-[11px] tracking-[0.2em] uppercase px-4 py-2 border transition-colors ${
              selectedVehicle === "all"
                ? "bg-[var(--brand-primary)] text-black border-[var(--brand-primary)]"
                : "text-white border-[var(--border-default)] hover:border-[var(--brand-primary)]"
            }`}
          >
            All Vehicles ({models.length})
          </button>
          {BULL_BAR_VEHICLES.map((v) => {
            const count = models.filter((m) => m.vehicle === v.key).length;
            return (
              <button
                key={v.key}
                onClick={() => setSelectedVehicle(v.key)}
                data-testid={`vehicle-filter-${v.key}`}
                className={`font-mono text-[11px] tracking-[0.2em] uppercase px-4 py-2 border transition-colors ${
                  selectedVehicle === v.key
                    ? "bg-[var(--brand-primary)] text-black border-[var(--brand-primary)]"
                    : "text-white border-[var(--border-default)] hover:border-[var(--brand-primary)]"
                }`}
              >
                {v.short} ({count})
              </button>
            );
          })}
        </div>
      </section>

      {/* VEHICLE-SECTIONED GRID */}
      <section className="container-x pb-16 md:pb-20">
        {groupedByVehicle.length === 0 && (
          <p className="text-[var(--text-secondary)] py-10 text-center font-mono text-xs uppercase tracking-[0.2em]">
            No products match. Try a different vehicle or brand filter.
          </p>
        )}
        {groupedByVehicle.map((group, gi) => {
          // Sub-group by type within vehicle
          const byType = BULL_BAR_TYPES.map((t) => ({
            type: t,
            items: group.items.filter((m) => m.type === t.key),
          })).filter((g) => g.items.length > 0);

          return (
            <div key={group.vehicle.key} id={group.vehicle.key} data-testid={`vehicle-section-${group.vehicle.key}`} className={`${gi > 0 ? "mt-16 pt-16 border-t border-[var(--border-default)]" : ""} scroll-mt-24`}>
              <div className="mb-6">
                <p className="overline mb-2">— {group.vehicle.note}</p>
                <h3 className="font-display text-2xl md:text-4xl text-white leading-tight">
                  {group.vehicle.label.toUpperCase()}
                </h3>
              </div>
              {byType.map((typeGroup) => (
                <div key={typeGroup.type.key} className="mb-8 last:mb-0">
                  <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--brand-primary)] mb-3">
                    {typeGroup.type.label} ({typeGroup.items.length})
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {typeGroup.items.map((m, i) => (
                      <Card key={m.slug} m={m} testid={`bullbar-${m.slug}`} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          );
        })}
      </section>
    </div>
  );
}
